import { create } from 'zustand';
import { softwareUpdatePackageService } from '@/lib/software-update-package.service';
import { softwareUpdatePackageKeys } from '@/hooks/use-software-update-packages';
import type { QueryClient } from '@tanstack/react-query';
import { notificationStore } from '@/stores/notification.store';
import { getAPIErrorMessage } from '@/lib/api';
import type { PackageUploadURLItem } from '@/lib/software-update-package.service';

export type UploadStage =
  | 'queued'
  | 'uploading'
  | 'creating'
  | 'completed'
  | 'error'
  | 'cancelled';

/** create = new package upload; change-file = replace the object of an existing package. */
export type UploadKind = 'create' | 'change-file';

export interface UploadJob {
  id: string;
  kind: UploadKind;
  /** Set for change-file jobs; the package whose file is being replaced. */
  packageId?: string;
  packageDefinitionLabel: string;
  packageName: string;
  packageType: 'apk' | 'os' | 'fw';
  version: string;
  description: string;
  machineTypes: string[];
  atsIds: string[];
  file: File;
  uploadFileName: string;
  upload: PackageUploadURLItem;
  progress: number;
  /** Smoothed upload speed in bytes/sec; undefined until enough samples. */
  speedBps?: number;
  /** Estimated seconds remaining at current speed. */
  etaSeconds?: number;
  stage: UploadStage;
  error?: string;
  startedAt: number;
  xhr?: XMLHttpRequest;
}

interface UploadState {
  jobs: UploadJob[];
  enqueue: (input: EnqueueInput) => string;
  enqueueChangeFile: (input: EnqueueChangeFileInput) => string;
  cancel: (id: string) => void;
  dismiss: (id: string) => void;
  clearAll: () => void;
}

interface EnqueueInput {
  packageDefinitionLabel: string;
  packageName: string;
  packageType: 'apk' | 'os' | 'fw';
  version: string;
  description?: string;
  machineTypes?: string[];
  atsIds?: string[];
  file: File;
  uploadFileName: string;
  upload: PackageUploadURLItem;
  queryClient: QueryClient;
}

interface EnqueueChangeFileInput {
  packageId: string;
  packageDefinitionLabel: string;
  packageName: string;
  packageType: 'apk' | 'os' | 'fw';
  version: string;
  description?: string;
  machineTypes?: string[];
  atsIds?: string[];
  file: File;
  uploadFileName: string;
  upload: PackageUploadURLItem;
  queryClient: QueryClient;
}

// Generate a stable enough id without pulling crypto in everywhere.
const newId = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `upl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`);

const errorMessage = (err: unknown) => {
  return getAPIErrorMessage(err, 'Upload failed');
};

const uploadWithXHR = (
  upload: { upload_url: string; method?: string; headers?: Record<string, string> },
  file: File,
  onProgress: (loaded: number, total: number) => void,
  registerXHR: (xhr: XMLHttpRequest) => void
) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    registerXHR(xhr);
    xhr.open(upload.method || 'PUT', upload.upload_url);
    Object.entries(upload.headers || {}).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    if (!upload.headers?.['Content-Type'] && file.type) {
      xhr.setRequestHeader('Content-Type', file.type);
    }
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      onProgress(event.loaded, event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else if (xhr.status === 412) reject(new Error('A file with this name already exists'));
      else reject(new Error(`Upload failed with status ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.onabort = () => reject(new Error('cancelled'));
    xhr.send(file);
  });

export const useUploadStore = create<UploadState>((set, get) => ({
  jobs: [],
  enqueue: ({
    packageDefinitionLabel,
    packageName,
    packageType,
    version,
    description = '',
    machineTypes = [],
    atsIds = [],
    file,
    uploadFileName,
    upload,
    queryClient,
  }) => {
    const id = newId();
    const job: UploadJob = {
      id,
      kind: 'create',
      packageDefinitionLabel,
      packageName,
      packageType,
      version,
      description,
      machineTypes,
      atsIds,
      file,
      uploadFileName,
      upload,
      progress: 0,
      stage: 'queued',
      startedAt: Date.now(),
    };
    set((state) => ({ jobs: [...state.jobs, job] }));
    void runJob(id, queryClient);
    return id;
  },
  enqueueChangeFile: ({
    packageId,
    packageDefinitionLabel,
    packageName,
    packageType,
    version,
    description = '',
    machineTypes = [],
    atsIds = [],
    file,
    uploadFileName,
    upload,
    queryClient,
  }) => {
    const id = newId();
    const job: UploadJob = {
      id,
      kind: 'change-file',
      packageId,
      packageDefinitionLabel,
      packageName,
      packageType,
      version,
      description,
      machineTypes,
      atsIds,
      file,
      uploadFileName,
      upload,
      progress: 0,
      stage: 'queued',
      startedAt: Date.now(),
    };
    set((state) => ({ jobs: [...state.jobs, job] }));
    void runJob(id, queryClient);
    return id;
  },
  cancel: (id) => {
    const job = get().jobs.find((j) => j.id === id);
    if (!job) return;
    if (job.stage === 'uploading' && job.xhr) {
      job.xhr.abort();
    }
    set((state) => ({
      jobs: state.jobs.map((j) =>
        j.id === id ? { ...j, stage: 'cancelled' as const, xhr: undefined } : j
      ),
    }));
  },
  dismiss: (id) =>
    set((state) => ({ jobs: state.jobs.filter((j) => j.id !== id) })),
  clearAll: () => set({ jobs: [] }),
}));

const updateJob = (id: string, patch: Partial<UploadJob>) => {
  useUploadStore.setState((state) => ({
    jobs: state.jobs.map((j) => (j.id === id ? { ...j, ...patch } : j)),
  }));
};

async function runJob(id: string, queryClient: QueryClient) {
  const job = useUploadStore.getState().jobs.find((j) => j.id === id);
  if (!job) return;

  try {
    if (useUploadStore.getState().jobs.find((j) => j.id === id)?.stage === 'cancelled') return;

    updateJob(id, { stage: 'uploading', progress: 0 });
    // Speed is computed over a sliding window of recent progress samples;
    // store writes are throttled since onprogress fires many times per second.
    const SPEED_WINDOW_MS = 5000;
    const UPDATE_INTERVAL_MS = 500;
    const samples: { t: number; loaded: number }[] = [];
    let lastUpdateAt = 0;
    let lastPercent = -1;
    await uploadWithXHR(
      job.upload,
      job.file,
      (loaded, total) => {
        const now = Date.now();
        samples.push({ t: now, loaded });
        while (samples.length > 1 && now - samples[0].t > SPEED_WINDOW_MS) samples.shift();
        const percent = Math.round((loaded / total) * 100);
        if (percent === lastPercent && now - lastUpdateAt < UPDATE_INTERVAL_MS) return;
        lastPercent = percent;
        lastUpdateAt = now;
        const first = samples[0];
        const elapsedSec = (now - first.t) / 1000;
        const speedBps =
          elapsedSec >= 0.5 && loaded > first.loaded
            ? (loaded - first.loaded) / elapsedSec
            : undefined;
        const etaSeconds = speedBps ? (total - loaded) / speedBps : undefined;
        updateJob(id, { progress: percent, speedBps, etaSeconds });
      },
      (xhr) => updateJob(id, { xhr })
    );

    if (useUploadStore.getState().jobs.find((j) => j.id === id)?.stage === 'cancelled') return;

    updateJob(id, { stage: 'creating', xhr: undefined, speedBps: undefined, etaSeconds: undefined });
    if (job.kind === 'change-file' && job.packageId) {
      await softwareUpdatePackageService.completeFileChange(job.packageId, {
        upload_id: job.upload.upload_id,
        package_name: job.packageName,
        type: job.packageType,
        version: job.version,
        description: job.description,
        machine_types: job.machineTypes,
        ats_ids: job.atsIds,
      });
    } else {
      await softwareUpdatePackageService.create({
        upload_id: job.upload.upload_id,
        package_name: job.packageName,
        type: job.packageType,
        version: job.version,
        description: job.description,
        machine_types: job.machineTypes,
        ats_ids: job.atsIds,
      });
    }

    updateJob(id, { stage: 'completed', progress: 100 });
    queryClient.invalidateQueries({ queryKey: softwareUpdatePackageKeys.lists() });
    if (job.kind === 'change-file' && job.packageId) {
      queryClient.invalidateQueries({
        queryKey: softwareUpdatePackageKeys.detail(job.packageId),
      });
    }
    notificationStore
      .getState()
      .show(
        job.kind === 'change-file'
          ? `${job.uploadFileName} file changed`
          : `${job.uploadFileName} uploaded`,
        'success'
      );
  } catch (err) {
    const current = useUploadStore.getState().jobs.find((j) => j.id === id);
    if (current?.stage === 'cancelled') return;
    if (err instanceof Error && err.message === 'cancelled') {
      updateJob(id, { stage: 'cancelled', xhr: undefined });
      return;
    }
    const message = errorMessage(err);
    updateJob(id, { stage: 'error', error: message, xhr: undefined });
    notificationStore.getState().show(`Upload failed: ${message}`, 'error');
  }
}
